import {
  CompositeFilterDescriptor,
  DataSourceRequestState,
  toDataSourceRequestString
} from '@progress/kendo-data-query';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { GridSortChangeEvent } from '@progress/kendo-react-grid';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RouteAnimation } from 'src/animations/animations';
import { SecondaryButton } from 'src/components/shared/Button/Button';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useReduxDispatch } from 'src/store';
import { getRequest } from 'src/store/api-handler';
import { MixtureDto } from 'src/store/mixtures/mixture';
import {
  deleteMixture,
  fetchFilteredMixtures,
  mixtureActions
} from 'src/store/mixtures/mixtures.actions';
import {
  selectFilteredMixtures,
  selectMixturesRequestState
} from 'src/store/mixtures/mixtures.selectors';
import { isLoadingMixtures } from 'src/store/requests/requests.selectors';
import { push } from 'src/utils/navigation';
import { MixturesList } from './MixturesList';

export const MixturesPage = () => {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const filteredMixtures = useSelector(selectFilteredMixtures);
  const requestState = useSelector(selectMixturesRequestState);
  const loadingMixtures = useSelector(isLoadingMixtures);

  const [mixture, setMixture] = useState<Nullable<MixtureDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showExportSpinner, setShowExportSpinner] = useState<boolean>(false);
  const [exporter, setExporter] = useState<Nullable<ExcelExport>>(null);

  const fetchMixtures = useCallback(
    (dataState: DataSourceRequestState) => {
      const queryStr = `${toDataSourceRequestString(dataState)}`;
      dispatch(fetchFilteredMixtures(queryStr));
    },
    [dispatch]
  );

  useEffect(() => {
    fetchMixtures(requestState);
  }, [requestState, fetchMixtures]);

  const onSortChange = useCallback(
    (event: GridSortChangeEvent) => {
      dispatch(
        mixtureActions.updateRequestState({
          ...requestState,
          sort: event.sort
        })
      );
    },
    [dispatch, requestState]
  );

  const onFilterChange = useCallback(
    (filter: CompositeFilterDescriptor | undefined) => {
      dispatch(
        mixtureActions.updateRequestState({
          ...requestState,
          filter: filter,
          skip: 0
        })
      );
    },
    [dispatch, requestState]
  );

  const onResetFilters = useCallback(() => {
    dispatch(
      mixtureActions.updateRequestState({
        ...requestState,
        filter: undefined,
        skip: 0
      })
    );
  }, [dispatch, requestState]);

  const onPageChange = useCallback(
    (skip: number, take: number) => {
      dispatch(
        mixtureActions.updateRequestState({
          ...requestState,
          skip,
          take
        })
      );
    },
    [dispatch, requestState]
  );

  const onCreate = () => {
    push('/mixtures/create', navigate, RouteAnimation.SlideRight);
  };

  const onEdit = (mixture: MixtureDto) => {
    push(`/mixtures/${mixture.id}`, navigate, RouteAnimation.SlideRight);
  };

  const onCancel = () => {
    setMixture(null);
    setShowDeleteModal(false);
  };

  const onDelete = (mixture: MixtureDto) => {
    setMixture(mixture);
    setShowDeleteModal(true);
  };

  const onConfirmDelete = async () => {
    if (mixture) {
      await dispatch(deleteMixture(mixture.id!, mixture));
      setMixture(null);
      setShowDeleteModal(false);
    }
  };

  const onInitExporter = (exporter: ExcelExport) => {
    setExporter(exporter);
  };

  const onExport = async () => {
    const newState = { ...requestState, take: undefined, skip: undefined };
    const queryStr = `${toDataSourceRequestString(newState)}`;
    setShowExportSpinner(true);
    const { data } = await getRequest<MixtureDto[]>(
      `/mixtures/filtered?${queryStr}`
    );
    exporter!.save(data);
    setShowExportSpinner(false);
  };

  return (
    <Fragment>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('mixture.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('mixture.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus" className="mr-32">
            {`${t('create.verb')} ${t('mixture.singular')}`}
          </SecondaryButton>
          <SecondaryButton
            onClick={onExport}
            loading={showExportSpinner}
            disabled={!exporter}
            icon="la-file-excel"
          >
            {`${t('export.excel')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <MixturesList
        data={filteredMixtures}
        loading={loadingMixtures}
        requestState={requestState}
        onEdit={onEdit}
        onDelete={onDelete}
        onSortChange={onSortChange}
        onFilterChange={onFilterChange}
        onResetFilters={onResetFilters}
        onPageChange={onPageChange}
        initExporter={onInitExporter}
      />

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('mixture.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('mixture.delete')} <q>{mixture?.description}</q>?
        </BodyType>
      </Modal>
    </Fragment>
  );
};
